import { useState } from "react";
import { HasAccess } from "@/components/common/has-access";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  History,
  Loader2,
  OctagonX,
  SkipForward,
  Square,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AuditLogEntry, AuditRun, downloadAutoFixExport } from "../services/audit-fix";
import { useAuditFixRun, useAutoFixRuns, useStopAutoFix } from "../hooks/use-audit-fix";
import { AuditTimestamp } from "./audit-timestamp";

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  AuditRun["status"],
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  PENDING: {
    label: "Pending",
    color: "text-gray-500",
    bg: "bg-gray-100",
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
  },
  RUNNING: {
    label: "Running",
    color: "text-blue-600",
    bg: "bg-blue-50",
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />,
  },
  STOPPING: {
    label: "Stopping…",
    color: "text-amber-600",
    bg: "bg-amber-50",
    icon: <Square className="h-3.5 w-3.5 text-amber-500" />,
  },
  STOPPED: {
    label: "Stopped",
    color: "text-amber-700",
    bg: "bg-amber-50",
    icon: <OctagonX className="h-3.5 w-3.5 text-amber-600" />,
  },
  COMPLETED: {
    label: "Completed",
    color: "text-green-700",
    bg: "bg-green-50",
    icon: <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />,
  },
  FAILED: {
    label: "Failed",
    color: "text-red-600",
    bg: "bg-red-50",
    icon: <XCircle className="h-3.5 w-3.5 text-red-500" />,
  },
};

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ value, max }: { value: number; max: number | null }) {
  const pct = max && max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-blue-500 transition-all duration-500 rounded-full"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── Log entry row ────────────────────────────────────────────────────────────

function LogRow({ log }: { log: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = (log.mappings?.length ?? 0) > 0 || (log.skipped?.length ?? 0) > 0;

  return (
    <div className="border border-gray-100 rounded-md overflow-hidden">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-left text-xs hover:bg-gray-50 transition-colors",
          !hasDetail && "cursor-default"
        )}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {log.mapped_count > 0 ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
          ) : (
            <SkipForward className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          )}
          <span className="font-medium text-gray-800 truncate">
            {log.invitee_name ?? log.identity_key}
          </span>
        </span>
        <span className="flex items-center gap-1.5 shrink-0 ml-2">
          {log.mapped_count > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-300 text-green-700">
              {log.mapped_count} mapped
            </Badge>
          )}
          {log.skipped_count > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-300 text-amber-700">
              {log.skipped_count} skipped
            </Badge>
          )}
          {hasDetail && (
            expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-2 space-y-1.5 bg-gray-50/60">
          {log.mappings && log.mappings.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide pt-1.5 pb-0.5">
                Mapped
              </p>
              {log.mappings.map((m) => (
                <div key={m.session_id} className="flex items-center gap-1.5 text-[11px] text-gray-700 py-0.5 flex-wrap">
                  <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                  <span className="truncate">{m.session_name}</span>
                  {m.session_start_time && (
                    <AuditTimestamp
                      value={m.session_start_time}
                      className="text-[10px] text-gray-400 shrink-0"
                    />
                  )}
                  <span className="text-gray-400 shrink-0">→</span>
                  <span className="text-gray-600 truncate">{m.bundle_name}</span>
                </div>
              ))}
            </div>
          )}
          {log.skipped && log.skipped.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide pt-1 pb-0.5">
                Skipped
              </p>
              {log.skipped.map((s) => (
                <div key={s.session_id} className="flex items-center gap-1.5 text-[11px] text-gray-500 py-0.5 flex-wrap">
                  <SkipForward className="h-3 w-3 text-amber-400 shrink-0" />
                  <span className="truncate">{s.session_name}</span>
                  {s.session_start_time && (
                    <AuditTimestamp
                      value={s.session_start_time}
                      className="text-[10px] text-gray-400 shrink-0"
                    />
                  )}
                  <span className="text-[10px] text-gray-400 shrink-0">
                    ({s.reason.replace(/_/g, " ")})
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Run detail view ──────────────────────────────────────────────────────────

interface RunDetailProps {
  runId: string;
  onBack?: () => void;
}

function RunDetail({ runId, onBack }: RunDetailProps) {
  const [logsPage, setLogsPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useAuditFixRun(runId, logsPage);
  const stopMutation = useStopAutoFix();

  const run = data?.run;
  const logs = data?.logs ?? [];
  const statusCfg = run ? STATUS_CONFIG[run.status] : null;
  const isActive = run?.status === "RUNNING" || run?.status === "STOPPING" || run?.status === "PENDING";
  const isStopping = run?.status === "STOPPING";
  const isTerminal = run?.status === "COMPLETED" || run?.status === "STOPPED" || run?.status === "FAILED";

  const handleExport = async () => {
    if (!run) return;
    setExporting(true);
    try {
      await downloadAutoFixExport(run.id);
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  if (!run) {
    return (
      <p className="text-sm text-muted-foreground text-center py-16">
        Run not found.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Run summary card */}
      <div className="rounded-lg border bg-gray-50 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className={cn("flex items-center gap-1.5 text-xs font-medium", statusCfg?.color)}>
            {statusCfg?.icon}
            {statusCfg?.label}
          </span>
          {run.started_at && (
            <AuditTimestamp
              value={run.started_at}
              className="text-[11px] text-muted-foreground"
            />
          )}
        </div>

        <ProgressBar value={run.processed_groups} max={run.total_groups} />

        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>
            {run.processed_groups}
            {run.total_groups ? ` / ${run.total_groups}` : ""} groups
          </span>
          {run.total_groups && run.total_groups > 0 && (
            <span>{Math.round((run.processed_groups / run.total_groups) * 100)}%</span>
          )}
        </div>

        <div className="flex gap-3">
          <div className="flex-1 rounded bg-green-50 px-2 py-1.5 text-center">
            <p className="text-base font-semibold text-green-700">{run.mapped_sessions}</p>
            <p className="text-[10px] text-green-600">Mapped</p>
          </div>
          <div className="flex-1 rounded bg-amber-50 px-2 py-1.5 text-center">
            <p className="text-base font-semibold text-amber-700">{run.skipped_sessions}</p>
            <p className="text-[10px] text-amber-600">Skipped</p>
          </div>
          <div className="flex-1 rounded bg-gray-100 px-2 py-1.5 text-center">
            <p className="text-base font-semibold text-gray-700">{run.processed_groups}</p>
            <p className="text-[10px] text-gray-500">Groups</p>
          </div>
        </div>

        {run.filters && (
          <div className="text-[11px] text-muted-foreground space-y-0.5 pt-1 border-t border-gray-100">
            {run.filters.date_from && <p>From: {run.filters.date_from}</p>}
            {run.filters.date_to && <p>To: {run.filters.date_to}</p>}
            {run.filters.exclude && <p>Excluded: {run.filters.exclude}</p>}
          </div>
        )}

        {run.error && (
          <p className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1">{run.error}</p>
        )}

        {/* Export CSV — terminal runs only */}
        {isTerminal && (
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" />Exporting…</>
            ) : (
              <><Download className="h-3.5 w-3.5" />Export CSV</>
            )}
          </Button>
        )}

        {/* Stop — active runs only */}
        {isActive && (
          <HasAccess permission="write">
          <Button
            size="sm"
            variant="outline"
            className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            disabled={isStopping || stopMutation.isPending}
            onClick={() => stopMutation.mutate(run.id)}
          >
            {isStopping ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Stopping…</>
            ) : (
              "Stop Fixing"
            )}
          </Button>
          </HasAccess>
        )}

        {run.completed_at && (
          <p className="text-[11px] text-muted-foreground text-center">
            {run.status === "COMPLETED" ? "Completed" : "Ended"}{" "}
            <AuditTimestamp value={run.completed_at} />
          </p>
        )}
      </div>

      {/* Group log */}
      {logs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Group Log
          </p>
          <div className="space-y-1.5">
            {logs.map((log) => <LogRow key={log.id} log={log} />)}
          </div>

          {(data?.log_last_page ?? 0) > 0 && (
            <div className="flex items-center justify-between pt-1">
              <Button size="sm" variant="ghost" disabled={logsPage === 0}
                onClick={() => setLogsPage((p) => p - 1)} className="text-xs h-7">
                Newer
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {logsPage + 1} / {(data?.log_last_page ?? 0) + 1}
              </span>
              <Button size="sm" variant="ghost" disabled={logsPage >= (data?.log_last_page ?? 0)}
                onClick={() => setLogsPage((p) => p + 1)} className="text-xs h-7">
                Older
              </Button>
            </div>
          )}
        </div>
      )}

      {logs.length === 0 && run.status === "RUNNING" && (
        <p className="text-xs text-center text-muted-foreground py-4">
          Processing first groups…
        </p>
      )}
    </div>
  );
}

// ─── History view ─────────────────────────────────────────────────────────────

interface HistoryViewProps {
  onSelectRun: (runId: string) => void;
}

function HistoryView({ onSelectRun }: HistoryViewProps) {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useAutoFixRuns(page);

  const runs = data?.runs ?? [];

  return (
    <div className="space-y-3">
      {isLoading && (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading history…
        </div>
      )}

      {!isLoading && runs.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-12">
          No past runs found.
        </p>
      )}

      {runs.map((run) => {
        const cfg = STATUS_CONFIG[run.status];
        const date = run.completed_at ?? run.started_at ?? run.created_at;
        return (
          <div key={run.id} className="rounded-lg border bg-white p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className={cn("flex items-center gap-1.5 text-xs font-medium", cfg.color)}>
                {cfg.icon}
                {cfg.label}
              </span>
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <AuditTimestamp value={date} />
              </span>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600">
              <span className="text-green-700 font-medium">{run.mapped_sessions} mapped</span>
              <span className="text-amber-700 font-medium">{run.skipped_sessions} skipped</span>
              <span className="text-gray-500">{run.processed_groups}{run.total_groups ? `/${run.total_groups}` : ""} groups</span>
            </div>

            <Button
              size="sm"
              variant="outline"
              className="w-full h-7 text-xs"
              onClick={() => onSelectRun(run.id)}
            >
              View Details
            </Button>
          </div>
        );
      })}

      {/* Pagination */}
      {(data?.last_page ?? 0) > 0 && (
        <div className="flex items-center justify-between pt-1">
          <Button size="sm" variant="ghost" disabled={page === 0}
            onClick={() => setPage((p) => p - 1)} className="text-xs h-7">
            Newer
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} / {(data?.last_page ?? 0) + 1}
          </span>
          <Button size="sm" variant="ghost" disabled={page >= (data?.last_page ?? 0)}
            onClick={() => setPage((p) => p + 1)} className="text-xs h-7">
            Older
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main drawer ──────────────────────────────────────────────────────────────

interface AuditFixDrawerProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  runId: string | null;
}

export function AuditFixDrawer({ open, onOpenChange, runId: initialRunId }: AuditFixDrawerProps) {
  const [view, setView] = useState<"detail" | "history">("detail");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId);

  // Keep in sync when the parent's active run changes
  if (initialRunId && initialRunId !== selectedRunId && view === "detail") {
    setSelectedRunId(initialRunId);
  }

  const handleSelectFromHistory = (id: string) => {
    setSelectedRunId(id);
    setView("detail");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <TooltipProvider delayDuration={200}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
          <SheetTitle className="text-sm font-semibold">Activity Log</SheetTitle>

          {view === "detail" ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs gap-1.5 text-muted-foreground mr-4"
              onClick={() => setView("history")}
            >
              <History className="h-3.5 w-3.5" />
              History
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs gap-1.5 text-muted-foreground mr-4"
              onClick={() => setView("detail")}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </Button>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {view === "history" ? (
            <HistoryView onSelectRun={handleSelectFromHistory} />
          ) : !selectedRunId ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              No run selected. Start an auto-fix to see activity here.
            </p>
          ) : (
            <RunDetail runId={selectedRunId} />
          )}
        </div>
      </SheetContent>
      </TooltipProvider>
    </Sheet>
  );
}
